import React, { SyntheticEvent, useContext, useState } from 'react'
import DotsLoader from 'shared/components/loaders/dots'
import styled from 'styled-components'
import { parse } from 'url'
import { ALL_ANIMATED_MIME_TYPES } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import CreationTool from '../../../types/creation-tool'
import CreationToolError from '../../../types/error'
import CreationToolOutputType from '../../../types/output-type'
import getSourceFrames from '../../../utils/get-source-frames'
import { getUrlMimeType } from '../../../utils/get-source-info'
import { validateSourceByInput } from '../../../utils/validate-source'
import { WelcomeBlock } from '../../blocks'
import { Subtitle, Text, TextInput } from '../../typography'
import { CreationToolsWelcomeContext } from './../context'
import { GiphyIcon, VimeoIcon, YoutubeIcon } from './icons'

const Wrapper = styled(WelcomeBlock)`
    position: relative;
`

const Description = styled(Text as any)`
    max-width: 100%;
`

const getMappedUrl = (source: string): string => {
    const mimeType = getUrlMimeType(source)

    // valid source
    if (mimeType) {
        return source
    }

    // giphy urls
    if (source.indexOf(`giphy.com`) > -1) {
        const isClip = source.indexOf('/clips/') > -1
        const filename = isClip ? `giphy360p.mp4` : `giphy.gif`
        const path = source
            .split('?')[0]
            .split('/')
            .pop()
        const gifId = path ? path.split('-').pop() : ''
        return gifId ? `https://media.giphy.com/media/${gifId}/${filename}` : source
    }

    return ''
}

const CreationWelcomeUrlInput = () => {
    const { tool, setFiles, trackEvent } = useContext(CreationToolsContext)
    const { setError, setErrorSource } = useContext(CreationToolsWelcomeContext)
    const [isValidating, setIsValidating] = useState<boolean>(false)

    const onInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { value = '' } = e.currentTarget
        const url = getMappedUrl(value)
        const requiresFrameProcessing = CreationTool.requiresFrameProcessing(tool)

        if (url) {
            setIsValidating(true)
            try {
                const validatedFile = await validateSourceByInput(url, ALL_ANIMATED_MIME_TYPES)
                let processedFile = validatedFile

                if (requiresFrameProcessing) {
                    const frames = await getSourceFrames({ sourceFile: validatedFile })
                    processedFile.frames = frames
                }

                setFiles([processedFile], CreationToolOutputType.Gif)
                setIsValidating(false)

                if (typeof validatedFile.sourceUrl === 'string') {
                    trackEvent(
                        undefined,
                        'Media URL',
                        parse((validatedFile.sourceUrl as unknown) as string).hostname || ''
                    )
                }
            } catch (err) {
                setError(err as CreationToolError)
                setErrorSource(value)
                trackEvent(
                    undefined,
                    CreationToolError.gaAction(err as CreationToolError, CreationToolOutputType.Gif),
                    CreationToolError.gaLabel(err as CreationToolError, url),
                    CreationToolError.gaValue(err as CreationToolError, url)
                )
                setIsValidating(false)
            }
        }
    }

    return (
        <Wrapper>
            <div style={isValidating ? { opacity: 0, pointerEvents: 'none' } : {}}>
                <Subtitle as="h3">Any URL</Subtitle>
                <Description>We support media URLs from GIPHY, YouTube, Vimeo, &amp; many others!</Description>
                <TextInput>
                    <input type="url" placeholder="Enter any media or GIF URL" onInput={onInput} />
                    <GiphyIcon />
                    <YoutubeIcon />
                    <VimeoIcon />
                </TextInput>
            </div>
            {isValidating && <DotsLoader isAbsolute />}
        </Wrapper>
    )
}

export default CreationWelcomeUrlInput
